/**
 * VarianceIntelligencePage — AI-powered variance narratives and drill-down.
 * Week selector, AI narrative card with key drivers, variance detail table,
 * and recommended actions.
 */

import React, { useState, useMemo } from 'react';
import { Card, CardContent } from '../../components/ui';
import { useForecastSummary, useVarianceIntelligence } from './api';
import { formatCurrency, formatPct } from './utils';
import { cn } from '../../shared/lib/cn';
import type { VarianceDetail, VarianceNarrative } from './types';

interface KeyDriver {
  driver: string;
  impact: number;
  direction: 'favorable' | 'unfavorable';
}

interface RecommendedAction {
  action: string;
  priority?: string;
  impact?: string;
}

function parseJSON<T>(raw: string | undefined | null, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function NarrativeCard({ narrative }: { narrative: VarianceNarrative }) {
  const drivers = parseJSON<KeyDriver[]>(narrative.key_drivers, []);
  const actions = parseJSON<RecommendedAction[]>(narrative.recommended_actions, []);

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-charcoal dark:text-gray-200">
            AI Variance Narrative — Week {narrative.week_number}
          </h3>
          <div className="flex items-center gap-2">
            {narrative.model_used && (
              <span className="px-2 py-0.5 rounded text-[10px] bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
                {narrative.model_used}
              </span>
            )}
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {new Date(narrative.generated_at).toLocaleDateString()}
            </span>
          </div>
        </div>

        <div className="prose prose-sm dark:prose-invert max-w-none mb-4">
          <p className="text-sm text-charcoal dark:text-gray-300 leading-relaxed whitespace-pre-line">
            {narrative.narrative_text}
          </p>
        </div>

        {drivers.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
              Key Drivers
            </p>
            <div className="flex flex-wrap gap-2">
              {drivers.map((d, i) => (
                <span
                  key={i}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
                    d.direction === 'favorable'
                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                      : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
                  )}
                >
                  <span>{d.direction === 'favorable' ? '↑' : '↓'}</span>
                  {d.driver}
                  {d.impact !== undefined && (
                    <span className="opacity-75">{formatCurrency(Math.abs(d.impact), true)}</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}

        {actions.length > 0 && (
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
              Recommended Actions
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {actions.map((a, i) => (
                <div
                  key={i}
                  className="p-3 rounded-lg border border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-surface-2/50"
                >
                  <p className="text-sm text-charcoal dark:text-gray-200">{a.action}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    {a.priority && (
                      <span
                        className={cn(
                          'px-1.5 py-0.5 rounded text-[10px] font-medium',
                          a.priority === 'high' || a.priority === 'critical'
                            ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                            : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
                        )}
                      >
                        {a.priority}
                      </span>
                    )}
                    {a.impact && (
                      <span className="text-[10px] text-gray-500 dark:text-gray-400">
                        {a.impact}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function VarianceTable({ details }: { details: VarianceDetail[] }) {
  if (details.length === 0) {
    return (
      <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-6">
        No variance detail for this week.
      </p>
    );
  }

  const sorted = [...details].sort(
    (a, b) => Math.abs(b.variance_amount) - Math.abs(a.variance_amount),
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 dark:border-dark-border">
            <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Line Item
            </th>
            <th className="py-2 px-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Forecast
            </th>
            <th className="py-2 px-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actual
            </th>
            <th className="py-2 px-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Variance
            </th>
            <th className="py-2 px-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Var %
            </th>
            <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Root Cause
            </th>
            <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Counterparty
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
          {sorted.map((d) => {
            const favorable = d.variance_amount >= 0;
            return (
              <tr
                key={d.id}
                className="hover:bg-gray-50 dark:hover:bg-dark-hover"
              >
                <td className="py-2 px-3 font-medium text-charcoal dark:text-gray-200">
                  {d.line_item}
                </td>
                <td className="py-2 px-3 text-right font-mono text-charcoal dark:text-gray-200">
                  {formatCurrency(d.forecast_amount)}
                </td>
                <td className="py-2 px-3 text-right font-mono text-charcoal dark:text-gray-200">
                  {formatCurrency(d.actual_amount)}
                </td>
                <td
                  className={cn(
                    'py-2 px-3 text-right font-mono font-semibold',
                    favorable
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-red-600 dark:text-red-400',
                  )}
                >
                  {favorable ? '+' : ''}
                  {formatCurrency(d.variance_amount)}
                </td>
                <td
                  className={cn(
                    'py-2 px-3 text-right font-mono',
                    favorable
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-red-600 dark:text-red-400',
                  )}
                >
                  {favorable ? '+' : ''}
                  {formatPct(d.variance_pct)}
                </td>
                <td className="py-2 px-3 text-gray-500 dark:text-gray-400 text-xs">
                  {d.root_cause_category && (
                    <span className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                      {d.root_cause_category}
                    </span>
                  )}
                  {d.root_cause_detail && (
                    <span className="ml-1">{d.root_cause_detail}</span>
                  )}
                  {!d.root_cause_category && !d.root_cause_detail && '—'}
                </td>
                <td className="py-2 px-3 text-gray-500 dark:text-gray-400">
                  {d.attributed_counterparty ?? '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function VarianceIntelligencePage() {
  const { data: summary, isLoading: summaryLoading } = useForecastSummary();
  const forecastId = summary?.forecast_id ?? '';
  const { data: viData, isLoading: viLoading } = useVarianceIntelligence(forecastId);

  const availableWeeks = useMemo(() => {
    if (!viData) return [] as number[];
    const weekSet = new Set<number>();
    viData.detail.forEach((d) => weekSet.add(d.week_number));
    viData.narratives.forEach((n) => weekSet.add(n.week_number));
    return Array.from(weekSet).sort((a, b) => a - b);
  }, [viData]);

  const [selectedWeek, setSelectedWeek] = useState<number | null>(null);
  const activeWeek = selectedWeek ?? availableWeeks[0] ?? 1;

  const weekDetails = useMemo(
    () => viData?.detail.filter((d) => d.week_number === activeWeek) ?? [],
    [viData, activeWeek],
  );

  const weekNarrative = useMemo(
    () => viData?.narratives.find((n) => n.week_number === activeWeek),
    [viData, activeWeek],
  );

  const isLoading = summaryLoading || viLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-gray-400">Loading variance intelligence...</div>
      </div>
    );
  }

  if (!viData || (viData.detail.length === 0 && viData.narratives.length === 0)) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <h2 className="text-lg font-semibold text-charcoal dark:text-gray-100 mb-2">
            Variance Intelligence
          </h2>
          <p className="text-gray-500 dark:text-gray-400">
            No variance data available. Actuals must be loaded for at least one completed
            forecast period.
          </p>
        </CardContent>
      </Card>
    );
  }

  const totalVariance = weekDetails.reduce((s, d) => s + d.variance_amount, 0);

  return (
    <div className="space-y-6 mt-2">
      {/* Week Selector */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        {availableWeeks.map((w) => (
          <button
            key={w}
            onClick={() => setSelectedWeek(w)}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap',
              w === activeWeek
                ? 'bg-charcoal text-white dark:bg-gray-200 dark:text-charcoal'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-dark-surface-2 dark:text-gray-300 dark:hover:bg-dark-border',
            )}
          >
            Week {w}
          </button>
        ))}
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
              Line Items
            </p>
            <p className="text-2xl font-bold text-charcoal dark:text-gray-100">
              {weekDetails.length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
              Net Variance
            </p>
            <p
              className={cn(
                'text-2xl font-bold',
                totalVariance >= 0
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-red-600 dark:text-red-400',
              )}
            >
              {totalVariance >= 0 ? '+' : ''}
              {formatCurrency(totalVariance, true)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
              Favorable
            </p>
            <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
              {weekDetails.filter((d) => d.variance_amount >= 0).length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
              Unfavorable
            </p>
            <p className="text-2xl font-bold text-red-600 dark:text-red-400">
              {weekDetails.filter((d) => d.variance_amount < 0).length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* AI Narrative */}
      {weekNarrative && <NarrativeCard narrative={weekNarrative} />}

      {/* Variance Table */}
      <Card>
        <CardContent className="p-6">
          <h3 className="text-sm font-semibold text-charcoal dark:text-gray-200 mb-4">
            Variance Detail — Week {activeWeek}
          </h3>
          <VarianceTable details={weekDetails} />
        </CardContent>
      </Card>
    </div>
  );
}
